import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { frontDeskApi } from '../../api/frontDesk';
import { luxisttConfirm } from '../../utils/luxisttSwal';
import { FdPerPageField, FdPaginationNav, normalizeFdPerPage } from '../../components/FdDataTableControls';

function slugifyName(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function initialAbilitiesForForm(row) {
  if (!row) return [];
  const raw = row.abilities_stored;
  if (Array.isArray(raw) && raw.length > 0) return [...raw];
  if (Array.isArray(row.abilities)) return [...row.abilities];
  return [];
}

function EmptyTableRow({ colSpan, message }) {
  return (
    <tr>
      <td colSpan={colSpan} className="fd-table-empty">
        <span>{message}</span>
      </td>
    </tr>
  );
}

/**
 * Filament-style permission role templates (optional per-property scope).
 * @param {{ propertyId?: string, propertyOptions?: { id: number, name: string }[], isPlatformAdmin?: boolean, canManage: boolean, setError: (s: string) => void, setSuccessMessage: (s: string) => void, refreshTick?: number }} props
 */
export function FrontDeskPermissionRolesPanel({
  propertyId = '',
  propertyOptions = [],
  isPlatformAdmin = false,
  canManage,
  setError,
  setSuccessMessage,
  refreshTick = 0,
}) {
  const [groups, setGroups] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [qDraft, setQDraft] = useState('');
  const [q, setQ] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    property_scope: '',
    sort_order: 0,
    is_active: true,
    abilities: [],
  });
  const [saving, setSaving] = useState(false);

  const loadGroups = useCallback(async () => {
    try {
      const res = await frontDeskApi.staffPermissionGroups();
      setGroups(Array.isArray(res.data) ? res.data : []);
    } catch {
      setGroups([]);
    }
  }, []);

  const loadRoles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (includeInactive && canManage) params.include_inactive = 1;
      if (propertyId) params.for_property = propertyId;
      const res = await frontDeskApi.staffPermissionRoles(params);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setRows([]);
      setError(e.message || 'Could not load permission roles.');
    } finally {
      setLoading(false);
    }
  }, [includeInactive, canManage, propertyId, setError]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles, refreshTick]);

  useEffect(() => {
    const t = setTimeout(() => setQ(qDraft.trim()), 300);
    return () => clearTimeout(t);
  }, [qDraft]);

  useEffect(() => {
    setPage(1);
  }, [q, includeInactive, perPage]);

  const filtered = useMemo(() => {
    const needle = q.toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => {
      const blob = `${r.name || ''} ${r.slug || ''} ${r.description || ''} ${r.property_name || ''}`.toLowerCase();
      return blob.includes(needle);
    });
  }, [rows, q]);

  const normPer = normalizeFdPerPage(perPage, 250);
  const pageCount = Math.max(1, Math.ceil(filtered.length / normPer));
  const safePage = Math.min(page, pageCount);
  const sliceFrom = (safePage - 1) * normPer;
  const pageRows = filtered.slice(sliceFrom, sliceFrom + normPer);
  const listMeta =
    filtered.length === 0
      ? null
      : {
          current_page: safePage,
          last_page: pageCount,
          per_page: normPer,
          total: filtered.length,
        };

  const openCreate = () => {
    setEditingId(null);
    setForm({
      name: '',
      slug: '',
      description: '',
      property_scope: isPlatformAdmin ? '' : propertyId ? String(propertyId) : '',
      sort_order: 0,
      is_active: true,
      abilities: [],
    });
    setEditorOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || '',
      slug: row.slug || '',
      description: row.description || '',
      property_scope:
        row.property_id != null && row.property_id !== '' ? String(row.property_id) : '',
      sort_order: row.sort_order ?? 0,
      is_active: row.is_active !== false,
      abilities: initialAbilitiesForForm(row),
    });
    setEditorOpen(true);
  };

  const toggleAbility = (value, checked) => {
    setForm((prev) => {
      const set = new Set(prev.abilities);
      if (checked) set.add(value);
      else set.delete(value);
      return { ...prev, abilities: [...set] };
    });
  };

  const saveRole = async () => {
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!isPlatformAdmin && !propertyId) {
      setError('Select a property in the header before creating a template.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const slug = form.slug.trim() || slugifyName(form.name);
      const body = {
        name: form.name.trim(),
        slug: slug || undefined,
        description: form.description.trim() || null,
        abilities: form.abilities,
        sort_order: Number(form.sort_order) || 0,
        is_active: Boolean(form.is_active),
      };
      if (isPlatformAdmin) {
        body.property_id =
          form.property_scope === '' || form.property_scope == null ? null : Number(form.property_scope);
      } else if (editingId) {
        if (form.property_scope !== '' && form.property_scope != null) {
          body.property_id = Number(form.property_scope);
        }
      } else {
        body.property_id = Number(propertyId);
      }
      if (editingId) {
        await frontDeskApi.updateStaffPermissionRole(editingId, body);
        setSuccessMessage('Permission role updated.');
      } else {
        await frontDeskApi.createStaffPermissionRole(body);
        setSuccessMessage('Permission role created.');
      }
      setEditorOpen(false);
      await loadRoles();
    } catch (e) {
      setError(e.message || 'Could not save role.');
    } finally {
      setSaving(false);
    }
  };

  const deleteRole = async (row) => {
    const ok = await luxisttConfirm({
      title: 'Delete role template?',
      text: `Remove “${row.name}”? Assignments using this template may need to be updated in admin.`,
      icon: 'warning',
      confirmText: 'Delete',
    });
    if (!ok) return;
    setError('');
    try {
      await frontDeskApi.deleteStaffPermissionRole(row.id);
      setSuccessMessage('Permission role deleted.');
      await loadRoles();
    } catch (e) {
      setError(e.message || 'Could not delete role.');
    }
  };

  return (
    <>
      <h2 className="front-desk-section-title">Roles &amp; permissions</h2>
      <p className="front-desk-section-lead">
        Create and edit permission templates (same bundles as Filament admin). Staff pick a template when assigned on the Users
        screen. “All properties” templates apply everywhere; scoped rows apply only to one building. Choose a property
        in the header to focus the list (and to create property-specific bundles if you are not a platform admin).
      </p>

      <div className="fd-bookings-toolbar" style={{ marginBottom: '1rem' }}>
        <label className="fd-bookings-toolbar-field">
          <span className="fd-bookings-toolbar-label">Search</span>
          <input type="search" value={qDraft} onChange={(e) => setQDraft(e.target.value)} placeholder="Name or slug" />
        </label>
        {canManage ? (
          <label className="fd-bookings-toolbar-field fd-bookings-toolbar-field--checkbox">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            <span>Show inactive</span>
          </label>
        ) : null}
        <FdPerPageField id="fd-perm-roles-per-page" value={perPage} onChange={setPerPage} maxAllowed={250} />
        {canManage ? (
          <div className="fd-bookings-toolbar-actions">
            <button type="button" className="btn-primary fd-btn-with-icon" onClick={openCreate}>
              <FiPlus aria-hidden />
              New role template
            </button>
          </div>
        ) : null}
      </div>

      {loading ? (
        <p className="muted">Loading…</p>
      ) : (
        <div className="fd-table-wrap">
          <table className="fd-table fd-table--wide">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Scope</th>
                <th className="fd-col-center">Abilities</th>
                <th className="fd-col-center">Sort</th>
                <th>Status</th>
                {canManage ? <th className="fd-actions-col-start">Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <EmptyTableRow colSpan={canManage ? 7 : 6} message="No role templates match your filters." />
              ) : (
                pageRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{row.name}</div>
                      {row.description ? (
                        <div className="muted" style={{ fontSize: '0.8125rem', maxWidth: 420 }}>
                          {row.description}
                        </div>
                      ) : null}
                    </td>
                    <td className="muted" style={{ fontSize: '0.875rem' }}>
                      {row.slug || '—'}
                    </td>
                    <td className="muted" style={{ fontSize: '0.875rem' }}>
                      {row.property_id != null && row.property_id !== ''
                        ? row.property_name || `Property #${row.property_id}`
                        : 'All properties'}
                    </td>
                    <td className="fd-col-center">{Array.isArray(row.abilities) ? row.abilities.length : 0}</td>
                    <td className="fd-col-center">{row.sort_order ?? 0}</td>
                    <td>
                      {row.is_active === false ? (
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 6,
                            background: 'rgba(107, 114, 128, 0.18)',
                            color: '#4b5563',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        >
                          Inactive
                        </span>
                      ) : (
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 6,
                            background: 'rgba(22, 163, 74, 0.14)',
                            color: '#15803d',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        >
                          Active
                        </span>
                      )}
                    </td>
                    {canManage ? (
                      <td className="fd-actions-col-start">
                        <div className="fd-finance-row-actions">
                          <button type="button" className="btn-ghost fd-finance-row-action" onClick={() => openEdit(row)}>
                            Edit
                          </button>
                          <button type="button" className="btn-ghost fd-finance-row-action" onClick={() => deleteRole(row)}>
                            Delete
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {listMeta ? (
        <FdPaginationNav meta={listMeta} page={safePage} setPage={setPage} disabled={loading} />
      ) : null}

      {editorOpen ? (
        <div
          className="modal-backdrop fd-modal-backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !saving) setEditorOpen(false);
          }}
        >
          <div
            className="modal-box modal-wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="fd-perm-role-editor-title"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 720 }}
          >
            <h3 id="fd-perm-role-editor-title">{editingId ? 'Edit role template' : 'New role template'}</h3>
            <div style={{ maxHeight: '65vh', overflowY: 'auto', marginTop: '0.5rem' }}>
              <div className="fd-finance-field-grid" style={{ marginBottom: '1rem' }}>
                <label className="fd-finance-field">
                  <span>Name</span>
                  <input
                    className="fd-finance-input"
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    disabled={saving}
                  />
                </label>
                <label className="fd-finance-field">
                  <span>Slug (optional)</span>
                  <input
                    className="fd-finance-input"
                    value={form.slug}
                    onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
                    placeholder="auto from name"
                    disabled={saving}
                  />
                </label>
                <label className="fd-finance-field fd-finance-field--full">
                  <span>Description</span>
                  <textarea
                    className="fd-finance-input"
                    rows={2}
                    value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                    disabled={saving}
                  />
                </label>
                {isPlatformAdmin ? (
                  <label className="fd-finance-field fd-finance-field--full">
                    <span>Property scope</span>
                    <select
                      className="fd-finance-input"
                      value={form.property_scope}
                      onChange={(e) => setForm((p) => ({ ...p, property_scope: e.target.value }))}
                      disabled={saving}
                      aria-label="Property scope"
                    >
                      <option value="">All properties</option>
                      {(propertyOptions || []).map((p) => (
                        <option key={p.id} value={String(p.id)}>
                          {p.name || `Property #${p.id}`}
                        </option>
                      ))}
                    </select>
                    <span className="muted" style={{ fontSize: '0.8125rem', display: 'block', marginTop: 4 }}>
                      Leave as “All properties” for a default bundle every building can use.
                    </span>
                  </label>
                ) : propertyId ? (
                  <p className="muted fd-finance-field--full" style={{ margin: 0, fontSize: '0.875rem' }}>
                    This template is scoped to the property selected in the header ({propertyOptions.find((p) => String(p.id) === String(propertyId))?.name || `ID ${propertyId}`}).
                  </p>
                ) : null}
                <label className="fd-finance-field">
                  <span>Sort order</span>
                  <input
                    type="number"
                    className="fd-finance-input"
                    value={form.sort_order}
                    min={0}
                    onChange={(e) => setForm((p) => ({ ...p, sort_order: e.target.value }))}
                    disabled={saving}
                  />
                </label>
                <label className="fd-finance-field fd-bookings-toolbar-field--checkbox" style={{ alignSelf: 'end' }}>
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                    disabled={saving}
                  />
                  <span>Active</span>
                </label>
              </div>

              <p className="muted" style={{ marginBottom: '0.75rem' }}>
                Select abilities (grouped like Filament). At least one is recommended.
              </p>
              {groups.map((g) => (
                <details
                  key={g.key}
                  className="fd-perm-role-group"
                  style={{ marginBottom: '0.75rem', border: '1px solid rgba(15, 118, 110, 0.2)', borderRadius: 8, padding: '0.5rem 0.75rem' }}
                  open
                >
                  <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{g.heading}</summary>
                  {g.description ? <p className="muted" style={{ fontSize: '0.8125rem', margin: '0.35rem 0' }}>{g.description}</p> : null}
                  <div style={{ display: 'grid', gap: '0.35rem', marginTop: '0.5rem' }}>
                    {(g.options || []).map((opt) => (
                      <label
                        key={opt.value}
                        style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.875rem' }}
                      >
                        <input
                          type="checkbox"
                          checked={form.abilities.includes(opt.value)}
                          onChange={(e) => toggleAbility(opt.value, e.target.checked)}
                          disabled={saving}
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </details>
              ))}
            </div>
            <div className="fd-booking-modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn-ghost" onClick={() => !saving && setEditorOpen(false)} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={saveRole} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
